import { memo, useCallback, useMemo } from 'react';

import { type SegmentedControlOption } from '@air/component-segmented-control';

import { SegmentedControlRow } from '~/components/ShareModal/components/SegmentedControlRow';
import { UpsellFlashIconButton } from '~/components/ShareModal/components/UpsellFlashIconButton';
import { SHARE_MODAL_UPSELL_MESSAGE } from '~/constants/upsells';
import { useShareSettings } from '~/hooks/useShareSettings';

export type CommentsRowProps = {
  canViewDiscussions: boolean;
  canManageDiscussions: boolean;
  canView: boolean;
  canManage: boolean;
  disabled?: boolean;
  isLoading: boolean;
  onChange: (next: { canView: boolean; canManage: boolean }) => void;
  'data-testid'?: string;
};

export const CommentsRow = memo(
  ({
    canViewDiscussions,
    canManageDiscussions,
    canView,
    canManage,
    disabled = false,
    isLoading,
    onChange,
    'data-testid': dataTestId,
  }: CommentsRowProps) => {
    const { commentsEnabled } = useShareSettings();

    const isGated = disabled || !commentsEnabled || isLoading;

    const selectedValue = useMemo(() => {
      if (canView && canManage) {
        return 'allow';
      }

      if (canView) {
        return 'read-only';
      }

      return 'hide';
    }, [canView, canManage]);

    const options = useMemo((): SegmentedControlOption[] => {
      const next: SegmentedControlOption[] = [
        { label: 'Hide', value: 'hide', id: 'hide', tooltip: 'Hide', disabled: isGated },
      ];

      const canSelectReadOnly = canViewDiscussions && !(canManage && !canManageDiscussions);
      const includeReadOnlyForState = selectedValue === 'read-only' && !canSelectReadOnly;

      if (canSelectReadOnly || includeReadOnlyForState) {
        next.push({
          label: 'Read-only',
          value: 'read-only',
          id: 'read-only',
          tooltip: 'Read-only',
          disabled: includeReadOnlyForState ? true : isGated,
        });
      }

      const includeAllowForState = selectedValue === 'allow' && !canManageDiscussions;

      if (canManageDiscussions || includeAllowForState) {
        next.push({
          label: 'Allow',
          value: 'allow',
          id: 'allow',
          tooltip: 'Allow',
          disabled: includeAllowForState ? true : isGated,
        });
      }

      return next;
    }, [canViewDiscussions, canManageDiscussions, canManage, isGated, selectedValue]);

    const selectedOption = useMemo(
      () => options.find((option) => option.value === selectedValue) ?? options[0],
      [options, selectedValue],
    );

    const handleSelect = useCallback(
      (option: SegmentedControlOption) => {
        if (option.value === 'hide') {
          onChange({ canView: false, canManage: false });
        } else if (option.value === 'read-only') {
          onChange({ canView: true, canManage: false });
        } else if (option.value === 'allow') {
          onChange({ canView: true, canManage: true });
        }
      },
      [onChange],
    );

    const labelAccessory = useMemo(
      () =>
        !commentsEnabled ? <UpsellFlashIconButton>{SHARE_MODAL_UPSELL_MESSAGE}</UpsellFlashIconButton> : undefined,
      [commentsEnabled],
    );

    return (
      <SegmentedControlRow
        data-testid={dataTestId}
        label="Comments"
        labelAccessory={labelAccessory}
        onSelect={handleSelect}
        options={options}
        selectedOption={selectedOption}
      />
    );
  },
);

CommentsRow.displayName = 'CommentsRow';
